import { useMemo, useState } from "react";
import { breadcrumbScript } from "@/lib/seo-jsonld";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { BadgeCheck, MapPin, Plus, Search, Store as StoreIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fetchActiveStores, storePlanLabels, storeSlug } from "@/lib/stores";

export const Route = createFileRoute("/magazalar")({
  head: () => ({
    meta: [
      { title: "Mağazalar — rəsmi satıcılar | Albazar.az" },
      {
        name: "description",
        content:
          "Albazar.az mağazaları: rəsmi satıcıların vitrinləri, məhsulları və əlaqə məlumatları. Öz mağazanı pulsuz yarat.",
      },
      { property: "og:title", content: "Mağazalar — rəsmi satıcılar | Albazar.az" },
      {
        property: "og:description",
        content: "Rəsmi mağazaların vitrinlərinə bax və ya öz mağazanı yarat.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://albazar.az/magazalar" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://albazar.az/magazalar" }],
    scripts: [breadcrumbScript([{ name: "Mağazalar", path: "/magazalar" }])],
  }),
  component: StoresPage,
});

function StoresPage() {
  const [q, setQ] = useState("");
  const { data: stores, isLoading } = useQuery({
    queryKey: ["stores", "active"],
    queryFn: fetchActiveStores,
  });

  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return stores ?? [];
    return (stores ?? []).filter((s) =>
      `${s.name} ${s.city ?? ""} ${s.description ?? ""}`.toLowerCase().includes(needle),
    );
  }, [stores, q]);

  return (
    <div className="container-x py-5 sm:py-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-extrabold tracking-tight sm:text-3xl">Mağazalar</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Rəsmi satıcıların vitrinləri — məhsullar, əlaqə və çatdırılma məlumatları.
          </p>
        </div>
        <Link to="/magazam">
          <Button className="h-10 rounded-full font-semibold">
            <Plus className="size-4" /> Mağaza yarat
          </Button>
        </Link>
      </div>

      <div className="relative mt-4">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Mağaza axtar..."
          aria-label="Mağaza axtar"
          className="h-11 rounded-full pl-9"
        />
      </div>

      {isLoading ? (
        <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-44 animate-pulse rounded-2xl bg-muted" />
          ))}
        </div>
      ) : list.length === 0 ? (
        <div className="mt-10 rounded-3xl border border-dashed border-border p-8 text-center">
          <StoreIcon className="mx-auto size-8 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">
            Hələ aktiv mağaza yoxdur. İlk mağazanı sən yarat!
          </p>
          <Link to="/magazam" className="mt-4 inline-block">
            <Button className="h-10 rounded-full">Mağaza yarat</Button>
          </Link>
        </div>
      ) : (
        <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
          {list.map((s) => (
            <Link
              key={s.id}
              to="/magaza/$slug"
              params={{ slug: storeSlug(s) }}
              className="group overflow-hidden rounded-2xl border border-border bg-card shadow-card transition-transform active:scale-[0.99]"
            >
              <div className="relative h-20 bg-gradient-to-br from-primary/25 to-primary/5 sm:h-24">
                {s.banner_url ? (
                  <img
                    src={s.banner_url}
                    alt={`${s.name} banner`}
                    loading="lazy"
                    className="size-full object-cover"
                  />
                ) : null}
                <div className="absolute -bottom-6 left-3 size-12 overflow-hidden rounded-xl border-2 border-card bg-muted">
                  {s.logo_url ? (
                    <img
                      src={s.logo_url}
                      alt={`${s.name} loqo`}
                      loading="lazy"
                      className="size-full object-cover"
                    />
                  ) : (
                    <span className="grid size-full place-items-center text-base font-black text-primary">
                      {s.name.slice(0, 1).toUpperCase()}
                    </span>
                  )}
                </div>
              </div>
              <div className="px-3 pb-3 pt-8">
                <div className="flex items-center gap-1">
                  <h2 className="truncate text-sm font-bold">{s.name}</h2>
                  {s.verified && <BadgeCheck className="size-4 shrink-0 text-info" />}
                </div>
                <p className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground">
                  <MapPin className="size-3" /> {s.city ?? "Azərbaycan"}
                  <span className="ml-auto rounded-full bg-accent px-2 py-0.5 font-semibold">
                    {storePlanLabels[s.plan] ?? s.plan}
                  </span>
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}